/**
 * Portals that block automated access (no live listings in the carousel).
 * These are sorted to the bottom of platform overviews.
 */
const BLOCKED_HOSTS = [
  // Blocken automatisierte Abrufe (403) bzw. laden Treffer erst im Browser
  "immowelt.de",
  "savills.de",
  "savills.com",
  "hamburg-business.com",
  "immobilienscout24.de",
  "immoscout24.de",
  "immonet.de",
  "jll.de",
  "jll.com",
  "cbre.de",
  "cbre.com",
  "engelvoelkers.com",
  "cushmanwakefield.com",
  "bnpparibas-real-estate.com",
  "realestate.bnpparibas.de",
  // Hamburg portals without machine-readable listing pages
  "frankhoffmann-immobilien.de",
  "meinestadt.de",
  "wohnglueck.de",
  "immosuchmaschine.de",
  "witthoeft.com",
  "kreativgesellschaft.org",
  "sprinkenhof.de",
  "gladigau-immobilien.de",
  "tempo-werk.de",
  // Stuttgart portals without machine-readable listing pages
  "bnppre.de",
  "immo.region-stuttgart.de",
  "region-stuttgart.de",
  "erfolgsraeume.de",
  "kip.net",
  "roomstr.de",
  // Lagerhalle-mieten portals without machine-readable listing pages
  "lagerhallen24.de",
  "master.de",
  "dip-immobilien.de",
  "greif-contzen.de",
  "storage24.com",
  "goodman.com",
  "segro.com",
  "panattonieurope.com",
  "p3parks.com",
  "immotek-berlin.de",
  // Köln / Düsseldorf / München / Frankfurt / Leipzig portals without machine-readable listing pages
  "ivd24immobilien.de",
  "aengevelt.com",
  "larbig-mortag.de",
  "emslander-co.de",
  "igenus.de",
  "fritschco.de",
  "lwb.de",
  "leipzig.ihk.de",
  "regus.com",
];



/** Portals whose listing pages are supported by the live-carousel scraper. */
const VERIFIED_LIVE_HOSTS = [
  "immobilie1.de",
  "colliers.de",
  "gsg.de",
  "kleinanzeigen.de",
  "ebay-kleinanzeigen.de",
  // Hamburg
  "grossmann-berger.de",
  "gac.hamburg",
  "sharednc.com",
  "immobilienmarkt.faz.net",
  // Stuttgart
  "pfund-gewerbeimmobilien.de",
  "warelog-realestate.de",
  "logivest.de",
  "gewerbeimmobilien-makler.com",
  // Lagerhalle mieten (verified scrapable listing pages)
  "realogis.de",
  "nai-apollo.de",
  "robertcspies.de",
  "hallen-berlin.de",
  "makler24.com",
  "ohne-makler.net",
  "sueddeutsche.de",
  "lagerflaeche.de",
];



const hostOf = (url: string): string => {
  try {
    return new URL(url).hostname.replace(/^www\./, "").toLowerCase();
  } catch {
    return "";
  }
};

/** True when the portal allows crawling live listings for the carousel. */
export const supportsLiveListings = (url: string): boolean => {
  const host = hostOf(url);
  if (!host) return false;
  return !BLOCKED_HOSTS.some((b) => host === b || host.endsWith(`.${b}`));
};

const liveListingPriority = (url: string): number => {
  const host = hostOf(url);
  if (!host) return 2;
  if (VERIFIED_LIVE_HOSTS.some((item) => host === item || host.endsWith(`.${item}`))) return 0;
  if (BLOCKED_HOSTS.some((item) => host === item || host.endsWith(`.${item}`))) return 2;
  return 1;
};

/**
 * Stable sort: portals with crawlable live listings first,
 * portals that block automated access (JLL, ImmoScout24, ...) last.
 */
export function sortByLiveListings<T extends { url: string }>(items: T[]): T[] {
  return items
    .map((item, index) => ({ item, index, priority: liveListingPriority(item.url) }))
    .sort((a, b) => a.priority - b.priority || a.index - b.index)
    .map((entry) => entry.item);
}
